import { type MeResponse, type PermissionCode, isAdminRole } from "@seagames/contracts";
import { groupGrants } from "../travel/access";

/**
 * Who sees what on the Food & Beverages pages (M12, PLAN §0 F8–F10, §4).
 * NONE of this decides anything: every read is scoped in SQL and every write
 * is re-checked by the API (`fnb-rules.ts`). It only picks which page, tabs
 * and buttons to draw, so nobody clicks into a page of 403s.
 *
 * Who someone IS comes from the active role, never from a permission (bug 7):
 * an ORGANIZATION login is a NOC and gets its own packed meals, never the
 * host desk, whatever its group holds.
 */

type Session = Pick<MeResponse, "active_role" | "group">;

/** The permissions that open the F&B page for a group (nav + route guard). */
export const FNB_PAGE_PERMISSIONS = ["fnb.order", "fnb.manage", "fnb.scan"] as const satisfies readonly PermissionCode[];

/** The host F&B desk: meal plan, dining halls, settings, deciding orders. Admins or an OPERATION group with `fnb.manage`. */
export const canManageFnb = (me: Session): boolean => isAdminRole(me.active_role) || (me.active_role === "OPERATION" && groupGrants(me, "fnb.manage"));

/** The kitchen door: scan meal passes. Admins or an OPERATION group with `fnb.scan`. */
export const canScanMeals = (me: Session): boolean => isAdminRole(me.active_role) || (me.active_role === "OPERATION" && groupGrants(me, "fnb.scan"));

/** A NOC that orders packed meals for its own delegation. */
export const isFnbNoc = (me: Session): boolean => me.active_role === "ORGANIZATION" && groupGrants(me, "fnb.order");

/** Sees the Takeaway orders list (host shape): the desk, or a host group that only orders (`fnb.order`, creates PENDING). */
export const canSeeHostOrders = (me: Session): boolean => canManageFnb(me) || (me.active_role === "OPERATION" && groupGrants(me, "fnb.order"));

/**
 * Which page a session gets at `/logistics/food-beverages`:
 *   host    Overview · Dining halls · Takeaway orders (admins, `fnb.manage`)
 *   noc     "Where your delegation eats" + "Packed meals" (ORGANIZATION with `fnb.order`)
 *   scan    the scan station only (`fnb.scan` without `fnb.manage`), plus the
 *           orders tab when the same group also orders
 *   orders  a host group with only `fnb.order`: the orders tab
 *   null    none — the guard sends it home
 */
export type FnbView = "host" | "noc" | "scan" | "orders";

export function fnbView(me: Session): FnbView | null {
	if (canManageFnb(me)) return "host";
	if (isFnbNoc(me)) return "noc";
	if (canScanMeals(me)) return "scan";
	if (canSeeHostOrders(me)) return "orders";
	return null;
}

/** The route guard and the nav: anyone with a view. */
export const canSeeFnb = (me: Session): boolean => fnbView(me) !== null;
